import React, { RefObject } from "react";
import { B2Tabs } from "@hdb/biome-2";
import TitleComponent from "src/shared/title/title.component";
import CustomSpinnerComponent from "src/shared/custom-spinner/custom-spinner.component";
import AnalyticsSplineChartComponent, {
  MINUTES_15_INTERVALS,
} from "./analytics-spline-chart.component";
import StepLineChartComponent from "./step-line-chart.component";

interface AnalyticsChartData {
  assetId: string;
  name: string;
  data: (number | null)[];
  lightIntensity?: (number | null)[];
}

interface AnalyticsChartComponentProps {
  showChart: boolean;
  showTitle?: boolean;
  selectedAssetCodes: string[];
  motionData: AnalyticsChartData[];
  daylightData: AnalyticsChartData[];
  lightOPStatusData: AnalyticsChartData[];
  isLoadingMotion: boolean;
  isLoadingDaylight: boolean;
  isLoadingLightOPStatus: boolean;
  tooltipOutside?: boolean;
  activeKey: string;
  onTabChange: (key: string) => void;
  renderKey: number;
  chartRef: RefObject<HTMLDivElement>;
  selectedDate: string;
}

const TAB_CONFIG = {
  Motion: {
    title: "Motion Traffic",
    yAxisTitle: "Motion Count",
    chartName: "Motion Analytics",
    xAxisCategories: MINUTES_15_INTERVALS,
    xAxisTitle: "Time (Hours)",
  },
  Daylight: {
    title: "Daylight",
    yAxisTitle: "Daylight Value (Lux)",
    chartName: "Daylight Analytics",
    xAxisCategories: MINUTES_15_INTERVALS,
    xAxisTitle: "Time (Hours)",
  },
  LightOPStatus: { title: "Light Status" },
};

const AnalyticsChartComponent: React.FC<AnalyticsChartComponentProps> = ({
  showChart,
  showTitle = true,
  selectedAssetCodes,
  motionData,
  daylightData,
  lightOPStatusData,
  isLoadingMotion,
  isLoadingDaylight,
  isLoadingLightOPStatus,
  tooltipOutside = true,
  activeKey,
  onTabChange,
  renderKey,
  chartRef,
  selectedDate,
}) => {
  if (!showChart) return null;

  const visibleAssetCodes = new Set(selectedAssetCodes);
  const filteredMotionData = motionData.filter((item) =>
    visibleAssetCodes.has(item.assetId),
  );
  const filteredDaylightData = daylightData.filter((item) =>
    visibleAssetCodes.has(item.assetId),
  );

  const filteredLightOPStatusData = lightOPStatusData.filter((item) =>
    visibleAssetCodes.has(item.assetId),
  );

  const currentConfig =
    TAB_CONFIG[activeKey as keyof typeof TAB_CONFIG] ||
    TAB_CONFIG.LightOPStatus;

  return (
    <div ref={chartRef}>
      {showTitle && (
        <div className="" style={{ margin: "5rem 0 2rem 0" }}>
          <TitleComponent title="Analytics" />
        </div>
      )}

      <div className="bg-card">
        <div>
          <h4 className="h4" style={{ marginBottom: "1rem" }}>
            {currentConfig.title}
          </h4>
        </div>

        <div className="custom_tab motion-traffic-responsive-tabs">
          <B2Tabs
            key={renderKey}
            activeKey={activeKey}
            pills
            onSelect={(selectedId: any) => {
              onTabChange(selectedId);
            }}
            tab={[
              {
                content: (
                  <CustomSpinnerComponent
                    height="400px"
                    width="100%"
                    isLoading={isLoadingLightOPStatus}
                  >
                    <StepLineChartComponent
                      data={filteredLightOPStatusData}
                      tooltipOutside={tooltipOutside}
                    />
                  </CustomSpinnerComponent>
                ),
                id: "LightOPStatus",
                title: "Light Status Vs Time",
              },
              {
                content: (
                  <CustomSpinnerComponent
                    height="400px"
                    width="100%"
                    isLoading={isLoadingDaylight}
                  >
                    <AnalyticsSplineChartComponent
                      seriesData={filteredDaylightData}
                      yAxisTitleText={TAB_CONFIG.Daylight.yAxisTitle}
                      chartName={TAB_CONFIG.Daylight.chartName}
                      xAxisCategories={TAB_CONFIG.Daylight.xAxisCategories}
                      xAxisTitle={TAB_CONFIG.Daylight.xAxisTitle}
                    />
                  </CustomSpinnerComponent>
                ),
                id: "Daylight",
                title: "Daylight Vs Time",
              },
              {
                content: (
                  <CustomSpinnerComponent
                    height="400px"
                    width="100%"
                    isLoading={isLoadingMotion}
                  >
                    <AnalyticsSplineChartComponent
                      seriesData={filteredMotionData}
                      yAxisTitleText={TAB_CONFIG.Motion.yAxisTitle}
                      chartName={TAB_CONFIG.Motion.chartName}
                      xAxisCategories={TAB_CONFIG.Motion.xAxisCategories}
                      xAxisTitle={TAB_CONFIG.Motion.xAxisTitle}
                    />
                  </CustomSpinnerComponent>
                ),
                id: "Motion",
                title: "Motion vs Time",
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default AnalyticsChartComponent;
